import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Package, Receipt, CurrencyDollar, Users, Plus, PencilSimple, Trash, X, UploadSimple, Spinner, Star, Eye, EyeSlash } from "@phosphor-icons/react";
import { COLOR_OPTIONS, swatchStyle } from "@/constants/colors";

const DEPARTMENTS = ["Manualidades y Artesanía", "Difusores", "Humidificadores", "Moldes de Silicona", "Fragancias", "Hogar", "Decoración", "Joyería", "Belleza", "Regalos"];

const CATEGORIES = {
  "Manualidades y Artesanía": ["Resina y moldes", "Velas y cera", "Cintas y lazos", "Flores y follaje", "Perlas y pedrería", "Herramientas y básicos"],
  "Moldes de Silicona": ["Moldes para velas", "Moldes para resina", "Moldes para jabón", "Moldes decorativos"],
  "Fragancias": ["Perfumes", "Difusores", "Velas", "Aceites esenciales"],
  "Difusores": ["Ultrasónicos", "De caña", "Eléctricos"],
  "Humidificadores": ["De ambiente", "Portátiles"],
  "Joyería": ["Collares", "Aretes", "Pulseras", "Anillos"],
  "Decoración": ["Jarrones", "Cuadros", "Accesorios"],
  "Hogar": ["Velas", "Textiles", "Organización"],
  "Belleza": ["Cuidado de piel", "Aromaterapia"],
  "Regalos": ["Sets de regalo", "Tarjetas"],
};

const emptyForm = { name: "", description: "", price: "", sale_price: "", department: "Manualidades y Artesanía", category: "", colors: [], tier_prices: [], images: [], stock: "", featured: false, visible: true };

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadAll = useCallback(async () => {
    setLoadError(false);
    let ok = false;
    for (let attempt = 0; attempt < 4 && !ok; attempt++) {
      try {
        const p = await api.get("/admin/products");
        setProducts(p.data);
        ok = true;
      } catch (e) {
        await new Promise((res) => setTimeout(res, 1500));
      }
    }
    setLoaded(true);
    if (!ok) { setLoadError(true); return; }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setError(""); setShowForm(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description, price: p.price, sale_price: p.sale_price ?? "", department: p.department, category: p.category || "", colors: p.colors || [], tier_prices: p.tier_prices || [], images: p.images || [], stock: p.stock, featured: p.featured, visible: p.visible !== false });
    setError("");
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true); setError("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((prev) => ({ ...prev, images: [...prev.images, data.url] }));
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Error al subir la imagen.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price) || 0,
      sale_price: (form.sale_price === "" || form.sale_price === null) ? null : parseFloat(form.sale_price),
      department: form.department,
      category: form.category,
      colors: form.colors,
      tier_prices: form.tier_prices
        .map((t) => ({ min_qty: parseInt(t.min_qty) || 0, price: parseFloat(t.price) || 0 }))
        .filter((t) => t.min_qty >= 2 && t.price > 0)
        .sort((a, b) => a.min_qty - b.min_qty),
      images: form.images,
      stock: parseInt(form.stock) || 0,
      featured: !!form.featured,
      visible: form.visible !== false,
    };
    try {
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      setShowForm(false);
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Error al guardar el producto.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/products/${id}`);
    loadAll();
  };

  const toggleFeatured = async (p) => {
    const next = !p.featured;
    if (next && p.visible === false) {
      if (!window.confirm("Este producto está OCULTO de la tienda (ojo apagado).\n\nPara salir en la página principal también debe estar publicado.\n\n¿Quiere publicarlo Y ponerlo en la portada ahora?")) return;
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: true, visible: true } : x));
      try {
        await api.patch(`/products/${p.id}/visible`, { visible: true });
        await api.patch(`/products/${p.id}/featured`, { featured: true });
      } catch {
        setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: false, visible: false } : x));
      }
      return;
    }
    setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: next } : x));
    try {
      await api.patch(`/products/${p.id}/featured`, { featured: next });
    } catch {
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, featured: !next } : x));
    }
  };

  const toggleVisible = async (p) => {
    const next = p.visible === false;
    setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, visible: next } : x));
    try {
      await api.patch(`/products/${p.id}/visible`, { visible: next });
    } catch {
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, visible: !next } : x));
    }
  };

  const statCards = [
    { label: "Productos", value: stats?.products ?? 0, icon: Package },
    { label: "Pedidos", value: stats?.orders ?? 0, icon: Receipt },
    { label: "Ingresos", value: `$${(stats?.revenue ?? 0).toFixed(2)}`, icon: CurrencyDollar },
    { label: "Clientes", value: stats?.customers ?? 0, icon: Users },
  ];

  const filtered = products.filter((p) => p.name.toLowerCase().includes(productSearch.toLowerCase()));
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <h1 className="font-serif text-4xl md:text-5xl text-stone-900 mb-8">Panel de administración</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white border border-stone-200 rounded-lg p-6" data-testid={`stat-${s.label}`}>
            <s.icon size={24} className="text-[#C0A062]" />
            <div className="text-2xl font-medium text-stone-900 mt-3">{s.value}</div>
            <div className="label-caps text-stone-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 border-b border-stone-200 mb-8">
        <button onClick={() => setTab("products")} className={`px-5 py-3 text-sm ${tab === "products" ? "border-b-2 border-[#3F4E4F] text-stone-900 font-medium" : "text-stone-500"}`} data-testid="tab-products">Productos</button>
        <button onClick={() => setTab("orders")} className={`px-5 py-3 text-sm ${tab === "orders" ? "border-b-2 border-[#3F4E4F] text-stone-900 font-medium" : "text-stone-500"}`} data-testid="tab-orders">Pedidos</button>
      </div>

      {tab === "products" && (
        <div>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <h2 className="font-serif text-2xl text-stone-900">Catálogo ({filtered.length})</h2>
            <div className="flex items-center gap-3">
              <input
                value={productSearch}
                onChange={(e) => { setProductSearch(e.target.value); setPage(1); }}
                placeholder="Buscar producto por nombre..."
                className="border border-stone-300 px-4 py-2.5 outline-none focus:border-[#3F4E4F] w-full md:w-64 text-sm"
                data-testid="admin-product-search"
              />
              <button onClick={openAdd} className="flex items-center gap-2 bg-[#3F4E4F] text-white px-5 py-3 hover:bg-[#2C3637] whitespace-nowrap" data-testid="add-product-button"><Plus size={18} /> Agregar producto</button>
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-3 bg-[#166534]/10 border border-[#166534]/30 rounded px-4 py-3 text-sm text-stone-700" data-testid="visible-hint">
              <Eye size={18} className="text-[#166534] shrink-0" />
              <span><span className="font-medium">Ojo verde</span> = el producto está PUBLICADO (los clientes lo ven en la tienda). <span className="font-medium">Ojo tachado gris</span> = OCULTO. Toque el ojo para publicar u ocultar.</span>
            </div>
            <div className="flex items-center gap-3 bg-[#C0A062]/10 border border-[#C0A062]/40 rounded px-4 py-3 text-sm text-stone-700" data-testid="featured-hint">
              <Star size={18} weight="fill" className="text-[#C0A062] shrink-0" />
              <span><span className="font-medium">Estrella dorada</span> = el producto además sale en la página principal (portada). <span className="font-medium">Estrella gris</span> = no sale en la portada.</span>
            </div>
          </div>
          {!loaded ? (
            <div className="bg-white border border-stone-200 rounded-lg p-16 text-center text-stone-400" data-testid="admin-loading">Cargando productos...</div>
          ) : loadError ? (
            <div className="bg-white border border-stone-200 rounded-lg p-12 text-center" data-testid="admin-load-error">
              <p className="text-stone-700 mb-2">No se pudieron cargar los productos en este momento.</p>
              <p className="text-stone-400 text-sm mb-6">El servidor puede estar ocupado unos segundos. Sus productos están guardados y seguros.</p>
              <button onClick={loadAll} className="bg-[#3F4E4F] text-white px-6 py-3 hover:bg-[#2C3637]" data-testid="admin-retry-button">Reintentar</button>
            </div>
          ) : (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EFEB] text-left">
                <tr className="label-caps text-stone-500">
                  <th className="px-5 py-3 font-normal">Producto</th>
                  <th className="px-5 py-3 font-normal hidden md:table-cell">Departamento</th>
                  <th className="px-5 py-3 font-normal">Precio</th>
                  <th className="px-5 py-3 font-normal hidden md:table-cell">Stock</th>
                  <th className="px-5 py-3 font-normal text-center">En tienda</th>
                  <th className="px-5 py-3 font-normal text-center">En portada</th>
                  <th className="px-5 py-3 font-normal text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {pageItems.map((p) => (
                  <tr key={p.id} className={p.visible === false ? "opacity-50" : ""} data-testid={`admin-product-${p.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover bg-stone-100" />
                        <span className="text-stone-900">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-stone-600 hidden md:table-cell">{p.department}</td>
                    <td className="px-5 py-3 text-stone-900">
                      {p.sale_price && p.sale_price < p.price ? (
                        <span><span className="text-[#991B1B]">${p.sale_price.toFixed(2)}</span> <span className="text-stone-400 line-through text-xs">${p.price.toFixed(2)}</span></span>
                      ) : `$${p.price?.toFixed(2)}`}
                    </td>
                    <td className="px-5 py-3 text-stone-600 hidden md:table-cell">{p.stock}</td>
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => toggleVisible(p)}
                        title={p.visible === false ? "Publicar en la tienda" : "Ocultar de la tienda"}
                        data-testid={`visible-toggle-${p.id}`}
                      >
                        {p.visible === false
                          ? <EyeSlash size={22} className="text-stone-300 hover:text-[#166534]" />
                          : <Eye size={22} weight="fill" className="text-[#166534]" />}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => toggleFeatured(p)}
                        title={p.featured ? "Quitar de la página principal" : "Poner en la página principal"}
                        data-testid={`featured-toggle-${p.id}`}
                      >
                        <Star size={22} weight={p.featured ? "fill" : "regular"} className={p.featured ? "text-[#C0A062]" : "text-stone-300 hover:text-[#C0A062]"} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(p)} className="text-stone-500 hover:text-[#3F4E4F]" data-testid={`edit-${p.id}`}><PencilSimple size={18} /></button>
                        <button onClick={() => remove(p.id)} className="text-stone-500 hover:text-[#991B1B]" data-testid={`delete-${p.id}`}><Trash size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-stone-200 bg-[#FAFAF8]" data-testid="admin-pagination">
              <span className="text-sm text-stone-500" data-testid="pagination-info">
                Mostrando {filtered.length === 0 ? 0 : (safePage - 1) * PER_PAGE + 1}–{Math.min(safePage * PER_PAGE, filtered.length)} de {filtered.length} productos
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                  disabled={safePage <= 1}
                  className="border border-stone-300 px-5 py-2.5 text-sm hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-prev"
                >← Anterior</button>
                <span className="text-sm text-stone-700 px-2" data-testid="pagination-page">Página {safePage} de {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                  disabled={safePage >= totalPages}
                  className="bg-[#3F4E4F] text-white px-5 py-2.5 text-sm hover:bg-[#2C3637] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-next"
                >Siguiente →</button>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F0EFEB] text-left">
              <tr className="label-caps text-stone-500">
                <th className="px-5 py-3 font-normal">Pedido</th>
                <th className="px-5 py-3 font-normal">Cliente</th>
                <th className="px-5 py-3 font-normal hidden md:table-cell">Fecha</th>
                <th className="px-5 py-3 font-normal">Total</th>
                <th className="px-5 py-3 font-normal">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {orders.length === 0 ? (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-stone-400">Aún no hay pedidos.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                  <td className="px-5 py-3 text-stone-900">#{o.id.slice(-8).toUpperCase()}</td>
                  <td className="px-5 py-3 text-stone-600">{o.user_email}</td>
                  <td className="px-5 py-3 text-stone-600 hidden md:table-cell">{new Date(o.created_at).toLocaleDateString("es")}</td>
                  <td className="px-5 py-3 text-stone-900">${o.amount?.toFixed(2)}</td>
                  <td className="px-5 py-3"><span className="bg-[#166534]/10 text-[#166534] text-xs px-3 py-1 rounded-full capitalize">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Product form modal */}
      {showForm && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="product-form-modal">
            <div className="flex justify-between items-center p-6 border-b border-stone-200">
              <h3 className="font-serif text-2xl text-stone-900">{editing ? "Editar producto" : "Nuevo producto"}</h3>
              <button onClick={() => setShowForm(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="label-caps text-stone-500 block mb-2">Nombre</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-name" />
              </div>
              <div>
                <label className="label-caps text-stone-500 block mb-2">Descripción</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps text-stone-500 block mb-2">Precio (USD)</label>
                  <input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-price" />
                </div>
                <div>
                  <label className="label-caps text-stone-500 block mb-2">Stock</label>
                  <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-stock" />
                </div>
              </div>
              <div>
                <label className="label-caps text-stone-500 block mb-2">Precio de oferta (opcional)</label>
                <input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} placeholder="Déjelo vacío si no hay oferta" className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-sale-price" />
                <p className="text-xs text-stone-400 mt-1">Si pone un precio menor al normal, el producto aparecerá en la sección <span className="text-[#991B1B] font-medium">Venta</span> con su etiqueta de descuento.</p>
              </div>
              <div>
                <label className="label-caps text-stone-500 block mb-2">Precios por cantidad (opcional)</label>
                {form.tier_prices.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2 flex-wrap" data-testid={`tier-row-${idx}`}>
                    <span className="text-sm text-stone-600">Desde</span>
                    <input type="number" min="2" value={t.min_qty} onChange={(e) => setForm({ ...form, tier_prices: form.tier_prices.map((x, i) => i === idx ? { ...x, min_qty: e.target.value } : x) })} className="w-16 border border-stone-300 px-2 py-1.5 outline-none focus:border-[#3F4E4F]" data-testid={`tier-qty-${idx}`} />
                    <span className="text-sm text-stone-600">unidades:</span>
                    <span className="text-sm text-stone-600">$</span>
                    <input type="number" step="0.01" min="0" value={t.price} onChange={(e) => setForm({ ...form, tier_prices: form.tier_prices.map((x, i) => i === idx ? { ...x, price: e.target.value } : x) })} className="w-24 border border-stone-300 px-2 py-1.5 outline-none focus:border-[#3F4E4F]" data-testid={`tier-price-${idx}`} />
                    <span className="text-sm text-stone-600">cada una</span>
                    <button type="button" onClick={() => setForm({ ...form, tier_prices: form.tier_prices.filter((_, i) => i !== idx) })} className="text-stone-400 hover:text-[#991B1B]" data-testid={`tier-remove-${idx}`}><X size={16} /></button>
                  </div>
                ))}
                {form.tier_prices.length < 2 && (
                  <button type="button" onClick={() => setForm({ ...form, tier_prices: [...form.tier_prices, { min_qty: form.tier_prices.length === 0 ? 2 : "", price: "" }] })} className="text-sm text-[#3F4E4F] border border-stone-300 px-3 py-1.5 hover:bg-stone-50" data-testid="add-tier-button">+ Agregar precio por cantidad</button>
                )}
                <p className="text-xs text-stone-400 mt-1">Ejemplo: 1 unidad paga el precio normal, y aquí pone "Desde 2 unidades: $4.00 cada una". El descuento se aplica solo en el carrito.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps text-stone-500 block mb-2">Departamento</label>
                  <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F] bg-white" data-testid="form-department">
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-caps text-stone-500 block mb-2">Categoría</label>
                  <input list="category-suggestions" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Elige o escribe una" className="w-full border border-stone-300 px-3 py-2.5 outline-none focus:border-[#3F4E4F]" data-testid="form-category" />
                  <datalist id="category-suggestions">
                    {(CATEGORIES[form.department] || []).map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
              <div>
                <label className="label-caps text-stone-500 block mb-2">Fotos del producto</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {form.images.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-24 group">
                      <img src={img} alt="" className="w-full h-full object-cover bg-stone-100 rounded" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-[#991B1B] text-white rounded-full p-1" data-testid={`remove-image-${idx}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  <label className="w-20 h-24 border-2 border-dashed border-stone-300 rounded flex flex-col items-center justify-center cursor-pointer hover:border-[#3F4E4F] text-stone-400 hover:text-[#3F4E4F]" data-testid="upload-image-label">
                    {uploading ? <Spinner size={20} className="animate-spin" /> : <><UploadSimple size={20} /><span className="text-[0.6rem] mt-1">Subir</span></>}
                    <input type="file" accept="image/*,.heic,.heif" multiple onChange={handleUpload} className="hidden" disabled={uploading} data-testid="upload-image-input" />
                  </label>
                </div>
                <p className="text-xs text-stone-400">Sube fotos desde tu celular o computadora (JPG, PNG, HEIC de iPhone). Las fotos grandes se ajustan solas.</p>
              </div>
              <div>
                <label className="label-caps text-stone-500 block mb-2">Colores disponibles (opcional)</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((c) => {
                    const active = form.colors.includes(c.name);
                    return (
                      <button
                        type="button"
                        key={c.name}
                        onClick={() => setForm({ ...form, colors: active ? form.colors.filter((x) => x !== c.name) : [...form.colors, c.name] })}
                        className={`flex items-center gap-2 border px-3 py-1.5 rounded-full text-sm transition-colors ${active ? "border-[#3F4E4F] bg-[#3F4E4F]/10 text-stone-900" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`}
                        data-testid={`color-option-${c.name}`}
                      >
                        <span className="inline-block w-4 h-4 rounded-full border border-stone-300" style={swatchStyle(c.name)} />
                        {c.name}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-stone-400 mt-1">Marque los colores en que viene el producto. Aparecerán como bolitas de color en la tienda.</p>
              </div>
              <label className="flex items-center gap-3 text-sm text-stone-700 border border-[#166534]/40 bg-[#166534]/10 rounded px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={form.visible !== false} onChange={(e) => setForm({ ...form, visible: e.target.checked })} data-testid="form-visible" />
                <span><span className="font-medium">Publicado en la tienda</span> — desmárquelo para dejarlo oculto mientras lo prepara</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-stone-700 border border-[#C0A062]/50 bg-[#C0A062]/10 rounded px-4 py-3 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
                <span><span className="font-medium">Mostrar en la página principal</span> — aparecerá en la portada de su tienda</span>
              </label>
              {error && <div className="text-[#991B1B] text-sm">{error}</div>}
              <button type="submit" disabled={saving} className="w-full bg-[#3F4E4F] text-white py-3 hover:bg-[#2C3637] disabled:opacity-50" data-testid="save-product-button">
                {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear producto"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
